import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ExternalLink, CalendarDays, Video, ChevronDown } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const DEFAULT_DAY1 = [
  "Clean installation & configuration of Mendeley Reference Manager on your laptop",
  "1-click article & PDF capture using Mendeley Web Importer with verified DOIs",
  "Building folder hierarchies, smart tags, and research collections for your topic",
  "Detecting and repairing missing/broken journal metadata in downloaded PDFs",
];
const DEFAULT_DAY2 = [
  "Integrating Mendeley Cite seamlessly with Microsoft Word & Google Docs",
  "Inserting single, multiple, and page-specific citations without document crashes",
  "Auto-generating clean bibliographies that update dynamically in real time",
  "Instant 1-click style shifts: APA 7th, IEEE, Harvard, Vancouver, Chicago & journal CSL formats",
];

export default function LiveSessions({ content }) {
  const highlights = content.workshop_highlights || [];
  const priceCurrent = content.workshop_price_current || "₹499";
  const priceFuture = content.workshop_price_future || "₹1,499";
  const priceLabel = content.workshop_price_label || "First Batch Price";

  const days = [
    {
      title: content.workshop_day1_title || "Day 1: Setup, Web Importer & Clean Metadata Ingestion",
      date: "21 October 2026 · 6:00–7:00 PM IST",
      items: content.workshop_day1_items?.length ? content.workshop_day1_items : DEFAULT_DAY1,
    },
    {
      title: content.workshop_day2_title || "Day 2: In-Text Citations, Dynamic Bibliographies & Style Switching",
      date: "22 October 2026 · 6:00–7:00 PM IST",
      items: content.workshop_day2_items?.length ? content.workshop_day2_items : DEFAULT_DAY2,
    },
  ];
  const [openDay, setOpenDay] = useState(0);

  return (
    <section id="live" data-testid="live-section" className="bg-white py-16 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <Overline>{content.workshop_overline}</Overline>
          <h2 className="mt-5 sm:mt-6 font-serif font-bold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]" data-testid="live-title">
            Live classes you can join on the day
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 sm:mt-14 relative overflow-hidden rounded-3xl bg-navy text-white" data-testid="workshop-feature-card">
            <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-cherry/20 blur-3xl" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 p-6 sm:p-12 lg:p-16">
              <div className="lg:col-span-7">
                <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                  <span className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] sm:tracking-[0.25em] bg-cherry/20 text-sky border border-cherry/30 px-3 sm:px-4 py-2 rounded-full">
                    <span className="h-2 w-2 rounded-full bg-cherry pulse-dot shrink-0" />
                    Registration Open
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.08em] sm:tracking-[0.2em] bg-white/10 text-sky border border-white/15 px-3 sm:px-4 py-2 rounded-full">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cherry shrink-0" />
                    <span>Udyam / MSME Registered<span className="hidden min-[420px]:inline"> · {content.udyam_id || "UDYAM-UP-66-0048291"}</span></span>
                  </span>
                </div>
                <h3 className="mt-5 sm:mt-6 font-serif text-xl sm:text-4xl font-bold leading-tight" data-testid="workshop-title">
                  {content.workshop_title}
                </h3>
                <div className="mt-4 sm:mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[13px] sm:text-sm font-sans text-white/70">
                  <span className="inline-flex items-center gap-2"><Video className="h-4 w-4 text-cherry shrink-0" />{content.workshop_duration}</span>
                  <span className="inline-flex items-center gap-2" data-testid="workshop-dates"><CalendarDays className="h-4 w-4 text-cherry shrink-0" />{content.workshop_dates}</span>
                </div>
                <p className="mt-5 sm:mt-6 text-sm sm:text-base font-sans text-white/60 leading-relaxed max-w-xl">
                  {content.workshop_note}
                </p>
                <div className="mt-6 sm:mt-8 flex flex-wrap items-end gap-4 sm:gap-5" data-testid="workshop-pricing">
                  <div>
                    <p className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] sm:tracking-[0.25em] text-sky">{priceLabel}</p>
                    <p className="mt-1.5 font-serif font-bold text-white leading-none">
                      <span className="text-3xl sm:text-5xl" data-testid="workshop-price-current">{priceCurrent}</span>
                      <span className="ml-2 sm:ml-3 text-base sm:text-xl text-white/40 line-through font-sans font-normal" data-testid="workshop-price-future">{priceFuture}</span>
                    </p>
                  </div>
                  <span className="mb-1 inline-flex items-center text-[10px] sm:text-[11px] font-mono uppercase tracking-widest bg-cherry/20 text-sky border border-cherry/30 px-3 py-1.5 rounded-full">
                    Limited Seats · Grab Yours First
                  </span>
                </div>
                <a
                  href={content.workshop_form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="workshop-register-button"
                  className="group mt-7 sm:mt-8 inline-flex items-center gap-3 bg-cherry hover:bg-[#B81919] text-white text-sm sm:text-base font-medium px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-colors duration-300"
                >
                  Register via Google Form
                  <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>

              <div className="lg:col-span-5 lg:border-l lg:border-white/10 lg:pl-10">
                <p className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.2em] sm:tracking-[0.25em] text-sky">What you'll learn</p>
                <ul className="mt-4 sm:mt-5 space-y-3.5 sm:space-y-4">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base font-sans text-white/85" data-testid={`workshop-highlight-${i}`}>
                      <CheckCircle2 className="h-5 w-5 text-cherry shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-6 sm:mt-8 grid gap-3 sm:gap-4" data-testid="daywise-breakdown">
            <p className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.2em] sm:tracking-[0.25em] text-navy/50">
              Full day-wise curriculum — tap to expand
            </p>
            {days.map((day, i) => {
              const isOpen = openDay === i;
              return (
                <div key={i} className="bg-chalk border border-navy/10 rounded-2xl overflow-hidden" data-testid={`day-card-${i + 1}`}>
                  <button
                    onClick={() => setOpenDay(isOpen ? -1 : i)}
                    data-testid={`day-toggle-${i + 1}`}
                    className="w-full flex items-center justify-between gap-4 px-5 sm:px-8 py-4 sm:py-5 text-left hover:bg-sky/20 transition-colors duration-300"
                  >
                    <div>
                      <p className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] sm:tracking-[0.25em] text-cherry">{day.date}</p>
                      <h4 className="mt-1.5 font-serif text-base sm:text-xl font-semibold text-navy leading-snug">{day.title}</h4>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-navy/50 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <ul className="px-5 sm:px-8 pb-5 sm:pb-6 space-y-3">
                          {day.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-3 text-sm sm:text-base font-sans text-[#5B6B7C] leading-relaxed" data-testid={`day${i + 1}-item-${j}`}>
                              <CheckCircle2 className="h-4 w-4 text-cherry shrink-0 mt-1" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
